<template>
  <div class="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-7xl mx-auto">
      <div v-if="loading" class="text-center text-gray-500 bg-white rounded-xl shadow-lg p-8"><p>Loading form...</p></div>
      <div v-else-if="error" class="bg-white rounded-xl shadow-lg p-8 text-center"><h2 class="text-2xl font-bold text-red-700">Error</h2><p class="mt-2 text-gray-600">{{ error }}</p></div>
      <div v-else-if="submissionSuccess" class="bg-white rounded-xl shadow-lg p-8 text-center">
        <div class="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center"><svg class="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg></div>
        <h2 class="mt-6 text-2xl font-bold text-gray-800">Thank You!</h2><p class="mt-2 text-gray-600">Your release form has been submitted successfully.</p>
      </div>
      <div v-else-if="client && submission" class="bg-white rounded-xl shadow-lg overflow-hidden">
        <div class="p-8">
          <div class="text-center mb-8">
            <img v-if="client.logoUrl" :src="client.logoUrl" :alt="client.podcastName" class="h-20 w-auto mx-auto mb-4">
            <h1 class="mt-4 text-3xl font-bold text-gray-800">Podcast Guest Release Agreement</h1>
            <p class="mt-2 text-gray-500 text-lg">for "{{ client.podcastName }}: {{ client.podcastSlogan }}"</p>
          </div>

          <div class="prose max-w-none text-gray-700 mb-8 p-4 bg-gray-50 rounded-md border" v-html="agreementTextHtml"></div>
          
          <div class="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-md space-y-3">
            <h2 class="text-lg font-semibold text-gray-800">Recording Details</h2>
            <div v-if="submission.podcastSubject">
              <p class="text-sm font-medium text-gray-500">Podcast Subject / Title</p>
              <p>{{ submission.podcastSubject }}</p>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-500">Date & Time</p>
              <p>{{ formattedRecordingDate }}</p>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-500">Recording Link</p>
              <a v-if="submission.recordingLink && submission.recordingLink.startsWith('http')" :href="submission.recordingLink" target="_blank" class="text-brand-purple-dark hover:text-brand-pink-static break-all">{{ submission.recordingLink }}</a>
              <p v-else-if="submission.recordingLink">{{ submission.recordingLink }}</p>
              <p v-else>Invitation link will be emailed soon.</p>
            </div>
          </div>
          <form @submit.prevent="submitForm" class="space-y-6">
            <h2 class="text-xl font-semibold text-gray-800 border-t pt-6">Your Details</h2>
            <div><label for="fullName" class="block text-sm font-medium text-gray-700">Full Name</label><input type="text" id="fullName" v-model="form.fullName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-purple-dark focus:border-brand-purple-dark"></div>
            <div><div class="flex justify-between items-center"><label class="block text-sm font-medium text-gray-700">Signature</label><button type="button" @click="clearSignature" class="text-sm text-brand-purple-dark hover:text-brand-pink-static">Clear</button></div><div class="mt-1 border border-gray-300 rounded-md"><Vue3Signature ref="signature" :sigOption="signatureOptions" :h="'200px'" class="w-full"></Vue3Signature></div><p v-if="signatureError" class="mt-2 text-sm text-red-600">Signature is required.</p></div>
            <div>
              <!-- START: DYNAMIC BUTTON COLOUR -->
              <button 
                type="submit" 
                :disabled="isSubmitting"
                :style="{ backgroundColor: client.brandColour || '#E7643B' }"
                class="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-purple-dark disabled:opacity-50">
                <span v-if="isSubmitting">Submitting...</span>
                <span v-else>Agree & Submit</span>
              </button>
              <!-- END: DYNAMIC BUTTON COLOUR -->
            </div>
          </form>
        </div>
         <div class="text-center py-4 px-8 bg-gray-50 border-t flex items-center justify-center space-x-2"><img src="@/assets/logo.png" alt="Castive Logo" class="h-5 w-auto"><p class="text-sm text-gray-500">Powered by Castive</p></div>
      </div>
    </div>
  </div>
</template>
<script>
import Vue3Signature from 'vue3-signature';
import { db } from '@/firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

export default {
  name: 'GuestReleaseForm',
  components: { Vue3Signature },
  data() {
    return {
      submission: null, client: null, loading: true, error: null,
      form: { fullName: '' },
      signatureOptions: { penColor: "#000000", backgroundColor: "#ffffff" },
      signatureError: false, isSubmitting: false, submissionSuccess: false
    };
  },
  computed: {
    agreementTextHtml() {
      if (!this.client) return '';
      return `<p>This agreement is made between <strong>The ${this.client.podcastName} Podcast</strong> ("Producer") and the individual completing this form ("Guest").</p><p>Guest agrees to participate in a recording for the podcast titled "${this.client.podcastName}". Producer may record, edit, and distribute the recording in audio, video, and written form.</p><p>Guest grants Producer a non-exclusive, worldwide, royalty-free right to use their name, voice, likeness, and contribution in the podcast and related promotional material. Guest waives any right to review or approve the final edited product.</p><p>Producer retains ownership of the podcast recording and all related intellectual property.</p><p>Guest acknowledges that no financial compensation is due for participation unless otherwise agreed in writing.</p><p>Guest releases Producer from any claims, demands, or causes of action arising from participation, recording, or distribution.</p>`;
    },
    formattedRecordingDate() {
      if (!this.submission || !this.submission.recordingDate?.toDate) return 'Not yet scheduled.';
      const date = this.submission.recordingDate.toDate();
      const options = { dateStyle: 'full', timeStyle: 'short' };
      return new Intl.DateTimeFormat('en-GB', options).format(date);
    }
  },
  methods: {
    clearSignature() {
      this.$refs.signature.clear();
      this.signatureError = false;
    },
    async submitForm() {
      if (this.$refs.signature.isEmpty()) { this.signatureError = true; return; }
      this.signatureError = false; this.isSubmitting = true;
      try {
        const signatureDataURL = this.$refs.signature.save('image/png');
        const submissionRef = doc(db, 'submissions', this.$route.params.id);
        await updateDoc(submissionRef, {
          guestName: this.form.fullName,
          guestSignature: signatureDataURL,
          status: 'pending_host_signature',
          submittedAt: serverTimestamp()
        });
        this.submissionSuccess = true;
      } catch (error) {
        console.error("Error updating document: ", error);
        alert('There was an error submitting your form. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  async created() {
    try {
      const submissionId = this.$route.params.id;
      if (!submissionId) { throw new Error("No submission ID provided."); }
      const submissionRef = doc(db, 'submissions', submissionId);
      const submissionSnap = await getDoc(submissionRef);
      if (!submissionSnap.exists()) { throw new Error("This release form link is invalid or has expired."); }
      this.submission = submissionSnap.data();
      this.form.fullName = this.submission.guestName;
      if (this.submission.status !== 'pending_guest_signature') { throw new Error("This release form has already been signed and submitted."); }
      const clientRef = doc(db, 'clients', this.submission.clientId);
      const clientSnap = await getDoc(clientRef);
      if (!clientSnap.exists()) { throw new Error("Could not load the associated client details."); }
      this.client = clientSnap.data();
    } catch (err) {
      this.error = err.message;
    } finally {
      this.loading = false;
    }
  }
};
</script>
<style scoped>
.prose :deep(p) { margin-bottom: 1em; }
.prose :deep(strong) { font-weight: bold; }
</style>